/**
 * Service de gestion des préférences du Dashboard.
 * Version 1 : localStorage
 * Architecture extensible pour migration future vers Supabase.
 */

import type { DashboardPreferences, DashboardWidgetId, SortableWidgetId } from '@/types/dashboard';
import { DEFAULT_DASHBOARD_PREFERENCES, DEFAULT_WIDGET_ORDER } from '@/types/dashboard';

export const DASHBOARD_PREFERENCES_STORAGE_KEY = 'luxori_dashboard_preferences';

/**
 * Charge les préférences depuis le stockage local.
 * Pour une future version Supabase : charger depuis la table user_preferences.
 */
export function loadDashboardPreferences(): DashboardPreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_DASHBOARD_PREFERENCES;
  }

  try {
    const stored = window.localStorage.getItem(DASHBOARD_PREFERENCES_STORAGE_KEY);
    if (!stored) return DEFAULT_DASHBOARD_PREFERENCES;

    const parsed = JSON.parse(stored) as Partial<DashboardPreferences>;
    return mergeWithDefaults(parsed);
  } catch {
    return DEFAULT_DASHBOARD_PREFERENCES;
  }
}

/**
 * Sauvegarde les préférences dans le stockage local.
 * Pour une future version Supabase : upsert dans user_preferences.
 */
export function saveDashboardPreferences(preferences: DashboardPreferences): void {
  if (typeof window === 'undefined') return;

  try {
    const toSave: DashboardPreferences = {
      ...preferences,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(DASHBOARD_PREFERENCES_STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.warn('Erreur lors de la sauvegarde des préférences Dashboard:', error);
  }
}

/**
 * Met à jour l'ordre des widgets (colonne principale).
 */
export function updateWidgetOrder(
  current: DashboardPreferences,
  newOrder: SortableWidgetId[]
): DashboardPreferences {
  return {
    ...current,
    widgetOrder: newOrder,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Met à jour la visibilité d'un widget.
 */
export function updateWidgetVisibility(
  current: DashboardPreferences,
  widgetId: DashboardWidgetId,
  enabled: boolean
): DashboardPreferences {
  return {
    ...current,
    widgets: {
      ...current.widgets,
      [widgetId]: enabled,
    },
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Fusionne les préférences chargées avec les valeurs par défaut.
 * Gère les migrations de schéma (nouveaux widgets, etc.).
 */
function mergeWithDefaults(partial: Partial<DashboardPreferences>): DashboardPreferences {
  const defaults = DEFAULT_DASHBOARD_PREFERENCES;
  const mergedWidgets = { ...defaults.widgets };
  let mergedOrder = [...defaults.widgetOrder];

  if (partial.widgets && typeof partial.widgets === 'object') {
    for (const [key, value] of Object.entries(partial.widgets)) {
      if (key in mergedWidgets && typeof value === 'boolean') {
        mergedWidgets[key as DashboardWidgetId] = value;
      }
    }
  }

  if (Array.isArray(partial.widgetOrder) && partial.widgetOrder.length > 0) {
    const validIds = new Set(DEFAULT_WIDGET_ORDER);
    const ordered = partial.widgetOrder.filter(id => validIds.has(id as SortableWidgetId));
    const missing = DEFAULT_WIDGET_ORDER.filter(id => !ordered.includes(id));
    mergedOrder = [...ordered, ...missing];
  }

  return {
    version: partial.version ?? defaults.version,
    widgets: mergedWidgets,
    widgetOrder: mergedOrder,
    updatedAt: partial.updatedAt ?? defaults.updatedAt,
  };
}
